/**
 * API Interceptor for PWA
 * Retries on 401 with token refresh, timeout for long requests
 */

import { customInstance } from '../api/client';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const REQUEST_TIMEOUT_MS = 15000;

export const apiWithRetry = async <T>({
  url,
  method,
  params,
  data,
  headers,
  signal: userSignal,
  retryCount = 0,
}: {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: Record<string, string | number | boolean | undefined>;
  data?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
  retryCount?: number;
}): Promise<T> => {
  const abortController = userSignal ? null : new AbortController();
  const timeoutId = abortController
    ? setTimeout(() => abortController.abort(), REQUEST_TIMEOUT_MS)
    : null;
  const signal = userSignal ?? abortController!.signal;

  try {
    const result = await customInstance<T>({
      url,
      method,
      params,
      data,
      headers,
      signal,
    });
    if (timeoutId) clearTimeout(timeoutId);
    return result;
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError('A requisição demorou muito. Verifique sua conexão.', 0);
    }

    const err = error as { status?: number };
    if (err?.status === 401 && retryCount === 0) {
      const { supabase } = await import('./supabase');
      if (supabase) {
        try {
          await supabase.auth.refreshSession();
          await new Promise((r) => setTimeout(r, 100));
          return apiWithRetry<T>({
            url,
            method,
            params,
            data,
            headers,
            signal: userSignal,
            retryCount: retryCount + 1,
          });
        } catch {
          throw error;
        }
      }
    }

    throw error;
  }
};
